import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, IconButton } from '@chakra-ui/react';
import { Delete, Edit } from '@material-ui/icons';

import { DistributorEmployeeLink } from '@app/api/models/distributor';
import Dialog from '@app/components/common/modals/dialog';
import { AutocompleteField, TextField, Txt } from '@app/components/common';
import { TableBase } from '@app/components/common/table/table-base';
import { useAlert, useApi, useAutocomplete, useEntityState } from '@app/hooks';

type LinkForm = {
  distributor?: EntityBase;
  code_ta: string;
};

const emptyForm: LinkForm = {
  code_ta: '',
};

export const EmployeeLinksTable = ({
  employeeId,
}: {
  employeeId: number;
}) => {
  const api = useApi();
  const { error, success } = useAlert();
  const getAutocomplete = useAutocomplete();

  const [links, setLinks] = useState<DistributorEmployeeLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DistributorEmployeeLink>();
  const [saving, setSaving] = useState(false);
  const [form, onChange, setForm] = useEntityState<LinkForm>(emptyForm);

  const isEdit = !!editing;

  const loadLinks = useCallback(async () => {
    if (!employeeId) {
      setLinks([]);
      return;
    }
    setLoading(true);
    try {
      const response = await api.distributors.getEmployeeLinks({
        employee_id: employeeId,
      });
      setLinks(response?.data || []);
    } catch (e) {
      error(e.message || 'Не удалось загрузить привязки');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const onAddClick = () => {
    setEditing(undefined);
    setForm(emptyForm);
    setShowForm(true);
  };

  const onEditClick = (item: DistributorEmployeeLink) => {
    setEditing(item);
    setForm({
      distributor: {
        id: item.distributor_id,
        name: item.distributor_name
          ? `[${item.distributor_id}] ${item.distributor_name}`
          : String(item.distributor_id),
      },
      code_ta: item.code_ta,
    });
    setShowForm(true);
  };

  const onCloseForm = () => {
    setShowForm(false);
    setEditing(undefined);
    setForm(emptyForm);
  };

  const onSave = async () => {
    if (!form.distributor?.id) {
      error('Выберите дистрибьютора');
      return;
    }
    if (!form.code_ta?.trim()) {
      error('Укажите код ТА');
      return;
    }

    const payload = {
      employee_id: employeeId,
      distributor_id: Number(form.distributor.id),
      code_ta: form.code_ta.trim(),
    };

    setSaving(true);
    try {
      if (isEdit && editing) {
        const deleteResponse = await api.distributors.deleteEmployeeLink({
          pid: editing.pid,
          is_active: false,
        });
        if (deleteResponse?.code !== 200) {
          error(deleteResponse?.message || 'Не удалось обновить привязку');
          return;
        }
      }

      const response = await api.distributors.createEmployeeLink(payload);
      if (response?.code === 200) {
        success(response.message || (isEdit ? 'Привязка обновлена' : 'Привязка добавлена'));
        onCloseForm();
        await loadLinks();
      }
    } catch (e) {
      error(e.message || (isEdit ? 'Не удалось обновить привязку' : 'Не удалось добавить привязку'));
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (pid: number) => {
    setLoading(true);
    try {
      const response = await api.distributors.deleteEmployeeLink({
        pid,
        is_active: false,
      });
      if (response?.code === 200) {
        success(response.message || 'Привязка удалена');
        await loadLinks();
      }
    } catch (e) {
      error(e.message || 'Не удалось удалить привязку');
      setLoading(false);
    }
  };

  return (
    <Box mt={-5} marginX={-4}>
      <TableBase<DistributorEmployeeLink>
        title="Привязки к коду ТА дистрибьютора"
        onCreateClick={onAddClick}
        keyProperty="pid"
        columns={[
          {
            label: 'Дистрибьютор',
            name: 'distributor_name',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (value, { rowData }) => (
                <Txt>
                  [{rowData.distributor_id}] {value || '—'}
                </Txt>
              ),
            },
          },
          {
            label: 'Код ТА',
            name: 'code_ta',
            options: {
              sort: false,
              filter: false,
            },
          },
          {
            label: 'Действия',
            name: 'pid',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (pid: number, { rowData }) => (
                <Flex gap={2}>
                  <IconButton
                    aria-label="edit"
                    onClick={() => onEditClick(rowData)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => onRemove(pid)}
                  >
                    <Delete />
                  </IconButton>
                </Flex>
              ),
            },
          },
        ]}
        data={links}
        loading={loading}
        paper={false}
      />

      <Dialog
        open={showForm}
        onClose={onCloseForm}
        title={isEdit ? 'Редактирование привязки' : 'Новая привязка'}
        width={480}
        actions={
          <>
            <Button
              colorScheme="brand"
              mr={3}
              onClick={onSave}
              isLoading={saving}
            >
              {isEdit ? 'Сохранить' : 'Добавить'}
            </Button>
            <Button variant="ghost" onClick={onCloseForm}>
              Отмена
            </Button>
          </>
        }
      >
        <Flex direction="column" gap={3}>
          <AutocompleteField
            id="distributor"
            label="Дистрибьютор"
            name="distributor"
            valueProperty="name"
            value={form.distributor}
            onFetchOptions={getAutocomplete}
            onChange={onChange('distributor')}
          />
          <TextField
            id="code_ta"
            label="Код ТА"
            value={form.code_ta}
            onChange={onChange('code_ta')}
          />
        </Flex>
      </Dialog>
    </Box>
  );
};
